import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function EditPatient() {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        name: '',
        birthdate: '',
        description: ''
    });
    const textareaRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    axios.defaults.withCredentials = true;

    useEffect(() => {
        const fetchPatient = async () => {
            try {
                const response = await axios.get(`${API_URL}patients/${id}`, {
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                const currentDescription = response.data.description || '';
                const updatedDescription = currentDescription;

                setFormData({
                    name: response.data.name,
                    birthdate: response.data.birthdate || '',
                    description: updatedDescription
                });
                setLoading(false);
            } catch (err) {
                setError('Failed to load patient data');
                setLoading(false);
            }
        };

        fetchPatient();
    }, [id, API_URL]);

    // Set cursor to the end of the textarea when formData.description changes
    useEffect(() => {
        if (textareaRef.current && formData.description) {
            const textarea = textareaRef.current;
            textarea.focus();
            textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            textarea.scrollTop = textarea.scrollHeight;
        }
    }, [formData.description]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSaving(true);

        try {
            // Add timestamp to the description
            const submissionData = {
                ...formData,
                description: formData.description
            };

            await axios.put(`${API_URL}patients/${id}`, submissionData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            navigate('/control/patients');
        } catch (err) {
            setError('Error updating patient: ' + (err.response?.data?.message || err.message));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="container mt-4">Loading patient data...</div>;
    }

    return (
        <div className="container mt-4">
            <div className="row justify-content-center">
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-header">
                            <h2 className="mb-0">Edit Patient</h2>
                        </div>
                        <div className="card-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}
                            <form onSubmit={handleSubmit} dir="ltr">
                                <div className="mb-3">
                                    <label htmlFor="name" className="form-label">Name</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="birthdate" className="form-label">Birth Date</label>
                                    <input
                                        type="date"
                                        className="form-control"
                                        id="birthdate"
                                        name="birthdate"
                                        value={formData.birthdate}
                                        onChange={handleChange}
                                        max={new Date().toISOString().split('T')[0]}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="description" className="form-label">Description</label>
                                    <textarea
                                        ref={textareaRef}
                                        className="form-control"
                                        id="description"
                                        name="description"
                                        rows="10"
                                        value={formData.description}
                                        onChange={handleChange}
                                        style={{
                                            whiteSpace: 'pre-wrap',
                                            minHeight: '300px',
                                            maxHeight: '60vh',
                                            resize: 'vertical',
                                            overflowY: 'auto'
                                        }}
                                    ></textarea>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <button 
                                        type="button" 
                                        className="btn btn-secondary" 
                                        onClick={() => navigate('/control/patients')}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
